import { ask } from "./atmosphere.ts";
import { type FormMeasures, measureForms } from "./forms.ts";
import type { Shape } from "./judge.ts";

/**
 * How a piece kneaded freely comes across to the kitten, in labels of three kinds: its air
 * (かわいい, 不思議), what it looks like (動物っぽい), and its shape (丸い). The model that
 * looks at a request's air (see atmosphere.ts) looks at photos of the piece for its air and
 * what it looks like; its shape is measured on the piece itself (see forms.ts). The labels
 * that stand out are put on it, and the shop's word on the delivery slip is made of them.
 * With no target, nothing is judged: the labels only say how the piece is seen.
 */

/** What the labels of a kind are asked by, and what else the model is given to say. */
interface Question {
  question: string;
  /** The start of the model's reply, written for it: a label's words are what comes next. */
  prefix: string;
  /**
   * Words of no label, which take up the model's plainer answers (a smooth, simple piece):
   * what is left for a label is then more nearly how much it is said.
   */
  others: string[];
}

interface Labelled {
  /** As the finish panel shows it. */
  label: string;
  /**
   * The mean and the spread across sample shapes of how much a piece has it: a label stands
   * out by how far a piece goes above its usual, in spreads.
   */
  usual: [number, number];
}

/** A label the model is asked about. */
interface Label extends Labelled {
  /**
   * What the model may say of a piece with it, in English, each one token after the reply's
   * start: how much it says them, together (the log of their share, over both photos), is how
   * much the label fits. The model says some words of anything, hence the usual.
   */
  words: string[];
}

/** An air, and how the shop's word says it (see sayOf). */
interface Air extends Label {
  /** Leading on to the next: かわいくて. */
  and: string;
  /** Before 作品: かわいらしい. */
  of: string;
}

/** A shape, measured on the piece (see forms.ts). */
interface Form extends Labelled {
  measure: keyof FormMeasures;
  /** What of it the shop's word says shows, and how: 丸み, 出て. */
  shows: [string, string];
  /**
   * Put on only when no other shape plainly stands out: a piece kneaded in symmetry is so,
   * whatever else it is.
   */
  fallback?: boolean;
}

// The words and the usual below were measured together, and hold only together: for another
// word, question, photo or measure, measure them again. The usual is over 53 sample shapes
// (see main.ts; the shapes' measures, over the 52 of them in one piece, as clay always is):
// the requests' targets and the clays they start from, ten made to have a label each (a
// ball, a rod, a slab, a thin stick, a spiky ball, a wavy sheet, a twisted bar, a cat's head,
// a car, a house), and thirty random lumps of balls and rods run together. The models looked
// at them fired.

/** The model is asked about the piece's air as adjectives it says of it. */
const AIR_QUESTION: Question = {
  question: "How does this look?",
  prefix: "It looks",
  others: ["smooth", "simple", "nice"],
};
const AIRS: Air[] = [
  {
    label: "かわいい",
    words: ["cute", "adorable", "lovely"],
    usual: [-3.12, 0.73],
    and: "かわいくて",
    of: "かわいらしい",
  },
  {
    label: "イケてる",
    words: ["cool", "stylish", "sleek"],
    usual: [-2.56, 0.62],
    and: "イケてて",
    of: "イケてる",
  },
  {
    label: "やさしい",
    words: ["gentle", "soft", "friendly", "tender"],
    usual: [-0.79, 0.41],
    and: "やさしくて",
    of: "やさしい",
  },
  {
    label: "おいしそう",
    words: ["delicious", "tasty", "yummy"],
    usual: [-3.65, 0.76],
    and: "おいしそうで",
    of: "おいしそうな",
  },
  {
    label: "古代っぽい",
    words: ["ancient", "old", "primitive"],
    usual: [-9.23, 0.79],
    and: "古代っぽくて",
    of: "古代っぽい",
  },
  {
    label: "不思議",
    words: ["mysterious", "strange", "magical", "weird"],
    usual: [-3.59, 0.69],
    and: "不思議で",
    of: "不思議な",
  },
  {
    label: "堂々としている",
    words: ["majestic", "grand", "dignified", "proud", "mighty", "noble"],
    usual: [-6.93, 0.73],
    and: "堂々としていて",
    of: "堂々とした",
  },
  {
    label: "ほっこりする",
    words: ["cozy", "heartwarming"],
    usual: [-6.29, 0.54],
    and: "ほっこりして",
    of: "ほっこりする",
  },
  {
    label: "たのしそう",
    words: ["fun", "playful", "cheerful", "happy"],
    usual: [-3.16, 0.78],
    and: "たのしそうで",
    of: "たのしそうな",
  },
  {
    label: "落ち着いている",
    words: ["calm", "peaceful", "quiet", "serene"],
    usual: [-4.47, 0.79],
    and: "落ち着いていて",
    of: "落ち着いた",
  },
  {
    label: "未来っぽい",
    words: ["futuristic", "modern", "robotic"],
    usual: [-5.18, 0.75],
    and: "未来っぽくて",
    of: "未来っぽい",
  },
  {
    label: "ぬくもりがある",
    words: ["warm"],
    usual: [-5.23, 0.58],
    and: "ぬくもりがあって",
    of: "ぬくもりのある",
  },
];
/** Airs that would make no sense said of one piece together. */
const CLASHING = [["古代っぽい", "未来っぽい"]];

/** What the piece looks like is asked as the kind of thing it is. */
const LOOK_QUESTION: Question = {
  question: "What does this look like?",
  prefix: "It looks like some kind of",
  others: ["sculpture", "rock", "ball", "figure"],
};
const LOOKS: Label[] = [
  { label: "顔っぽい", words: ["face", "mask", "head"], usual: [-6.35, 0.64] },
  {
    label: "動物っぽい",
    words: ["animal", "cat", "dog", "bird", "bear", "fish", "rabbit", "pig", "dragon"],
    usual: [-4.3, 0.68],
  },
  {
    label: "器っぽい",
    words: ["bowl", "vase", "cup", "pot", "jar", "mug", "jug"],
    usual: [-4.63, 0.75],
  },
  {
    label: "乗り物っぽい",
    words: ["vehicle", "car", "boat", "train", "ship", "rocket", "truck"],
    usual: [-6.43, 0.81],
  },
  {
    label: "建物っぽい",
    words: ["building", "house", "tower", "castle", "hut", "temple"],
    usual: [-7.96, 1.11],
  },
  { label: "道具っぽい", words: ["tool", "spoon", "key", "brush"], usual: [-5.51, 0.73] },
  {
    label: "食べ物っぽい",
    words: ["food", "bread", "fruit", "cake", "pastry", "dessert", "snack", "cookie", "candy"],
    usual: [-1.02, 0.56],
  },
  {
    label: "飾り物っぽい",
    words: ["ornament", "decoration", "jewel", "charm"],
    usual: [-5.7, 0.7],
  },
  { label: "古代の遺物っぽい", words: ["relic", "artifact", "fossil"], usual: [-5.79, 0.75] },
  { label: "おもちゃっぽい", words: ["toy", "doll"], usual: [-1.04, 0.54] },
];

/**
 * Its shape is measured on the piece: the model hardly tells a waist, a twist or a symmetry
 * (see forms.ts for each measure).
 */
const FORMS: Form[] = [
  { label: "丸い", measure: "round", usual: [0.775, 0.155], shows: ["丸み", "出て"] },
  { label: "細長い", measure: "long", usual: [0.483, 0.341], shows: ["細長さ", "出て"] },
  { label: "どっしり", measure: "stout", usual: [-0.516, 0.608], shows: ["どっしり感", "出て"] },
  {
    label: "ひょろっとしている",
    measure: "lanky",
    usual: [0.007, 0.507],
    shows: ["ひょろっと感", "出て"],
  },
  { label: "くびれがある", measure: "waist", usual: [0.089, 0.152], shows: ["くびれ", "出て"] },
  {
    label: "とげとげしている",
    measure: "spiky",
    usual: [0.162, 0.863],
    shows: ["とげとげ", "出て"],
  },
  {
    label: "左右対称っぽい",
    measure: "symmetric",
    usual: [0.23, 0.32],
    shows: ["左右のつり合い", "取れて"],
    fallback: true,
  },
  {
    label: "ひらひらしている",
    measure: "frilly",
    usual: [0.012, 0.082],
    shows: ["ひらひら感", "出て"],
  },
  { label: "まっすぐ", measure: "straight", usual: [0.161, 0.211], shows: ["まっすぐさ", "出て"] },
  { label: "ねじれている", measure: "twisted", usual: [0.181, 0.603], shows: ["ねじれ", "出て"] },
];

/**
 * A label put on the piece, and how strongly: 0 faintly (under a spread above its usual), 1
 * plainly, 2 strongly (two spreads above and more).
 */
export interface Tag<L extends Labelled = Labelled> {
  def: L;
  level: number;
}

/**
 * The labels put on a piece: the air that stands out most, and a second if it plainly stands
 * out too; what it looks like most, and its shape most.
 */
export interface Tags {
  airs: Tag<Air>[];
  look: Tag;
  form: Tag<Form>;
}

/** How much above its usual each label is of `photos`, in spreads, best first. */
async function standing<L extends Label>(
  photos: ImageData[],
  { question, prefix, others }: Question,
  labels: L[],
  loading: (share: number) => void,
): Promise<{ def: L; spreads: number }[]> {
  const words = [...labels.flatMap((l) => l.words), ...others];
  const shares = await Promise.all(
    photos.map((photo) => ask(photo, question, words.map((w) => " " + w), loading, prefix)),
  );
  return labels
    .map((def) => {
      const logs = shares.map((s) =>
        Math.log(def.words.reduce((sum, w) => sum + s[words.indexOf(w)], 0))
      );
      return { def, spreads: spreadsOf(def, logs.reduce((a, b) => a + b, 0) / logs.length) };
    })
    .sort((a, b) => b.spreads - a.spreads);
}

const spreadsOf = ({ usual: [mean, spread] }: Labelled, value: number) => (value - mean) / spread;

const tagOf = <L extends Labelled>({ def, spreads }: { def: L; spreads: number }): Tag<L> => ({
  def,
  level: spreads >= 2 ? 2 : spreads >= 1 ? 1 : 0,
});

/** The shape `shape` has most (see Form.fallback). */
function formOf(shape: Shape): Tag<Form> {
  const measures = measureForms(shape);
  const forms = FORMS
    .map((def) => ({ def, spreads: spreadsOf(def, measures[def.measure]) }))
    .sort((a, b) => b.spreads - a.spreads);
  const plain = forms.find((f) => !f.def.fallback)!;
  return tagOf(forms[0].def.fallback && plain.spreads >= 1 ? plain : forms[0]);
}

/**
 * The labels of the piece in `photos`, whose surface is `shape` (see Tags). The model is
 * loaded on first use, reporting how much of it has come by `loading`.
 */
export async function rateTags(
  photos: ImageData[],
  shape: Shape,
  loading: (share: number) => void,
): Promise<Tags> {
  const [airs, looks] = await Promise.all([
    standing(photos, AIR_QUESTION, AIRS, loading),
    standing(photos, LOOK_QUESTION, LOOKS, loading),
  ]);
  // Measured once the model has answered, not while the panel comes in: it takes a moment.
  const form = formOf(shape);
  const [first] = airs;
  const clash = (a: Air) =>
    CLASHING.some((pair) => pair.includes(first.def.label) && pair.includes(a.label));
  const second = airs.slice(1).find((a) => a.spreads >= 1 && !clash(a.def));
  return {
    airs: [first, ...second ? [second] : []].map(tagOf),
    look: tagOf(looks[0]),
    form,
  };
}

/** How strongly each kind is said on the slip, faintly to strongly (see Tag.level). */
const AIR_DEGREES = ["どこか", "", "かなり"];
const LOOK_DEGREES = ["少し", "", "かなり"];
const FORM_DEGREES = ["ほどよく", "よく", "しっかり"];

/**
 * The shop's word on the slip of a piece with `tags`, as "かなり不思議で、かわいらしい作品です。
 * 丸みもよく出ていて、少し動物っぽくも見えます".
 */
export function sayOf({ airs: [first, second], look, form }: Tags): string {
  const air = AIR_DEGREES[first.level] +
    (second ? `${first.def.and}、${second.def.of}` : first.def.of);
  const [shown, how] = form.def.shows;
  const like = look.def.label.replace(/い$/, "く");
  return `${air}作品です。${shown}も${FORM_DEGREES[form.level]}${how}いて、` +
    `${LOOK_DEGREES[look.level]}${like}も見えます`;
}
